import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "./cn"
import { Loader2 } from "lucide-react";

/*
  The focus ring is NOT declared here any more.

  It used to be `focus-visible:outline-hidden focus-visible:ring-2 ring-ring
  ring-offset-2`, which meant Button was the only focusable thing in the app with
  an indicator — every link, every hand-rolled `<button>` and every one of the six
  landing CTAs had none. There is now one `:focus-visible` rule in globals.css that
  covers all of them, and the `outline-hidden` here was actively suppressing it.
  Do not reintroduce a per-component ring.
*/
const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-ui font-semibold active:scale-[0.98] transition-transform duration-150 ease-out disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        /*
          No shadow on either fill: a button at rest is paper lying flat (ADR 0011).
          The primary one takes the landing plate's scissor cut — see the compound
          variants below.
        */
        default: "bg-accent text-on-accent hover:bg-accent/90",
        destructive:
          "bg-danger text-on-danger hover:bg-danger/90",
        outline:
          "border border-hairline-strong bg-transparent text-text-1 hover:bg-surface",
        secondary:
          "bg-surface-strong text-text-1 hover:bg-surface-strong/80",
        ghost: "text-text-2 hover:bg-surface hover:text-text-1",
        link: "text-accent-text underline-offset-4 hover:underline",
        muted:
          "bg-surface-strong border border-hairline text-text-3 pointer-events-none active:scale-100",

        /*
          ── Marketing chrome ────────────────────────────────────────────────
          The three below exist because the landing page's CTAs were six
          hand-rolled class lists, which is why the focus ring never reached
          them. They reproduce those class lists exactly; DESIGN.md §6 scopes
          `shadow-cta` to marketing, so do not reach for `cta` inside /y.
          Issue 08 owns any change to how these actually look.
        */
        cta: "bg-accent text-on-accent font-medium shadow-cta transition-opacity hover:opacity-90",
        ctaOutline:
          "border border-hairline bg-transparent font-normal text-text-2 transition-colors hover:border-hairline-strong hover:text-text-1",
        /*
          The room-section chip. Carries geometry and motion only — its fill,
          border and text colour arrive as the section's tone (ROOM_TONE in
          pages/landing/constants.ts), because the tone is per-section
          data and cannot be three more variants.
        */
        tonal: "border text-body font-medium transition-opacity hover:opacity-80",
      },
      size: {
        default: "py-[15px] px-4",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        cta: "px-8 py-3.5",
        chip: "px-6 py-2.5",
      },
    },
    /*
      The plate cut (`plate-cut` in packages/theme/tokens.css): the primary button's
      right edge is slanted like the landing's CTA plate. Only on the text sizes —
      an icon-only square with one slanted side reads as a rendering bug, not a cut.
      Each size adds 0.5em of right padding to buy back what the slant takes, so the
      label stays optically centred; a caller that passes its own `px-*` overrides
      that and accepts a slightly off-centre label.
    */
    compoundVariants: [
      { variant: "default", size: "default", class: "plate-cut pr-[calc(1rem+0.5em)]" },
      { variant: "default", size: "sm", class: "plate-cut pr-[calc(0.75rem+0.5em)]" },
      { variant: "default", size: "lg", class: "plate-cut pr-[calc(2rem+0.5em)]" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        data-slot="button"
        data-variant={variant}
        data-size={size}
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {/*
          `asChild` hands `children` straight through. Radix's Slot calls
          React.Children.only, so the loading/children pair below — an array,
          even when `loading` is false — threw "Slot failed to slot onto its
          children" for every consumer that tried it. A CTA that has to be an
          <a> (middle click, new tab, crawler) is exactly that consumer, and
          a spinner belongs to a button that is doing work, not to a link.
        */}
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {!loading && children}
          </>
        )}
      </Comp>
    );
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
